import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Globe,
  Github,
  ExternalLink,
  Rocket,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Copy,
  CheckCircle,
  Upload,
  Pencil,
  Film,
  Code,
  Link as LinkIcon,
  AlertCircle,
  Loader2,
  X,
  Monitor,
} from 'lucide-react';

interface Deployment {
  id: string;
  status: string;
  triggeredBy: string;
  commitSha: string | null;
  commitMsg: string | null;
  createdAt: string;
  duration: number | null;
}

interface Video {
  id: string;
  filename: string;
  originalName: string;
  status: 'PROCESSING' | 'READY' | 'FAILED';
  size: number | null;
  originalSize: number;
  duration: number | null;
  width: number | null;
  height: number | null;
  url: string;
  thumbnailUrl: string;
  errorMessage: string | null;
  createdAt: string;
}

interface Site {
  id: string;
  name: string;
  slug: string;
  domain: string;
  domains: string[];
  description: string | null;
  status: string;
  githubRepo: string | null;
  githubBranch: string;
  sourceDir: string | null;
  buildCommand: string | null;
  outputDir: string | null;
  maintenanceMsg: string | null;
  createdAt: string;
  deployments: Deployment[];
  hasDatabase: boolean;
  previewUrl: string;
}

export default function SiteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [uploadingZip, setUploadingZip] = useState(false);
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [videos, setVideos] = useState<Video[]>([]);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState<string | null>(null);

  const fetchVideos = (siteId: string) => {
    fetch(`/api/sites/${siteId}/videos`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : [])
      .then(setVideos)
      .catch(() => {});
  };

  useEffect(() => {
    if (id) fetchVideos(id);
  }, [id]);

  // Poll for processing videos
  useEffect(() => {
    const hasProcessing = videos.some((v) => v.status === 'PROCESSING');
    if (!hasProcessing || !id) return;
    const interval = setInterval(() => fetchVideos(id), 5000);
    return () => clearInterval(interval);
  }, [videos, id]);

  useEffect(() => {
    fetch(`/api/sites/${id}`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) {
          throw new Error(`Failed to load site (${r.status})`);
        }
        return r.json();
      })
      .then((data) => {
        setSite(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load site');
        setLoading(false);
      });
  }, [id]);

  const toggleMaintenance = async () => {
    if (!site) return;
    const enabled = site.status !== 'MAINTENANCE';
    await fetch(`/api/sites/${id}/maintenance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    setSite({ ...site, status: enabled ? 'MAINTENANCE' : 'ACTIVE' });
  };

  const triggerDeploy = async () => {
    setDeploying(true);
    await fetch(`/api/sites/${id}/deploy`, { method: 'POST' });
    // Refresh
    const res = await fetch(`/api/sites/${id}`);
    setSite(await res.json());
    setDeploying(false);
  };

  const deleteSite = async () => {
    if (!confirm('Are you sure you want to delete this site? This cannot be undone.')) {
      return;
    }
    await fetch(`/api/sites/${id}`, { method: 'DELETE' });
    navigate('/sites');
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedValue(label);
    setTimeout(() => setCopiedValue(null), 2000);
  };

  const handleZipUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.name.endsWith('.zip')) {
      alert('Please select a .zip file');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      alert('File too large (max 50MB)');
      return;
    }

    const formData = new FormData();
    formData.append('zipFile', file);

    setUploadingZip(true);
    try {
      const res = await fetch(`/api/sites/${id}/upload-zip`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        alert(`ZIP uploaded! Deployment started. Deployment ID: ${data.deploymentId}`);
        // Refresh site data to show new deployment
        const siteRes = await fetch(`/api/sites/${id}`);
        setSite(await siteRes.json());
      } else {
        const errorData = await res.json();
        alert(`Upload failed: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingZip(false);
      // Reset the file input
      e.target.value = '';
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 500 * 1024 * 1024) {
      alert('File too large (max 500MB)');
      return;
    }

    const formData = new FormData();
    formData.append('videoFile', file);

    setUploadingVideo(true);
    setVideoUploadProgress(`Uploading ${file.name}...`);
    try {
      const res = await fetch(`/api/sites/${id}/videos`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        setVideoUploadProgress(`Processing ${data.filename}...`);
        if (id) fetchVideos(id);
      } else {
        const errorData = await res.json();
        alert(`Upload failed: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(null);
      e.target.value = '';
    }
  };

  const deleteVideo = async (videoId: string, filename: string) => {
    if (!confirm(`Delete video "${filename}"? This cannot be undone.`)) return;

    try {
      const res = await fetch(`/api/sites/${id}/videos/${videoId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok && id) {
        fetchVideos(id);
      } else {
        alert('Failed to delete video');
      }
    } catch {
      alert('Failed to delete video');
    }
  };

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return '-';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '-';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const statusColors: Record<string, string> = {
    ACTIVE: 'text-green-400 bg-green-400/10',
    MAINTENANCE: 'text-yellow-400 bg-yellow-400/10',
    DISABLED: 'text-gray-400 bg-gray-400/10',
    DEPLOYING: 'text-blue-400 bg-blue-400/10',
    SUCCESS: 'text-green-400 bg-green-400/10',
    FAILED: 'text-red-400 bg-red-400/10',
    RUNNING: 'text-blue-400 bg-blue-400/10',
    PENDING: 'text-yellow-400 bg-yellow-400/10',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-red-400 text-lg">
          {error || 'Failed to load site'}
        </div>
        <div className="text-gray-400 text-sm">
          You may need to <a href="/auth/github" className="text-primary-400 hover:underline">log in</a> to access this site
        </div>
        <Link
          to="/sites"
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
        >
          Back to Sites
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link
        to="/sites"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Sites
      </Link>

      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gray-800">
            <Globe className="w-8 h-8 text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{site.name}</h1>
            <div className="flex items-center gap-3 text-gray-400">
              <div className="flex items-center gap-2">
                <span>{site.domain}</span>
                <a
                  href={`https://${site.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary-400"
                  title="Visit production site"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <div className="h-4 w-px bg-gray-600" />
              <a
                href={site.previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm hover:text-primary-400 transition-colors"
                title="Preview site"
              >
                <Globe className="w-4 h-4" />
                Preview
              </a>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 hover:text-white transition-colors"
          >
            <Pencil className="w-4 h-4" />
            Edit
          </button>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              statusColors[site.status]
            }`}
          >
            {site.status}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick actions */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Quick Actions
            </h2>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={toggleMaintenance}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                {site.status === 'MAINTENANCE' ? (
                  <>
                    <ToggleRight className="w-5 h-5 text-yellow-400" />
                    Disable Maintenance
                  </>
                ) : (
                  <>
                    <ToggleLeft className="w-5 h-5" />
                    Enable Maintenance
                  </>
                )}
              </button>

              {site.githubRepo && (
                <button
                  onClick={triggerDeploy}
                  disabled={deploying}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 rounded-lg text-white"
                >
                  <Rocket className="w-5 h-5" />
                  {deploying ? 'Deploying...' : 'Deploy Now'}
                </button>
              )}

              <label className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-lg text-white cursor-pointer">
                <Upload className="w-5 h-5" />
                {uploadingZip ? 'Uploading...' : 'Upload ZIP'}
                <input
                  type="file"
                  accept=".zip"
                  onChange={handleZipUpload}
                  disabled={uploadingZip}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* GitHub Integration */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              GitHub Integration
            </h2>
            {site.githubRepo ? (
              <div className="flex items-center gap-3">
                <Github className="w-5 h-5 text-gray-400" />
                <div>
                  <a
                    href={`https://github.com/${site.githubRepo}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-400 hover:text-primary-300"
                  >
                    {site.githubRepo}
                  </a>
                  <p className="text-sm text-gray-500">Branch: {site.githubBranch}</p>
                  {(site.sourceDir || site.buildCommand || site.outputDir) && (
                    <div className="mt-2 text-sm text-gray-500 space-y-0.5">
                      {site.sourceDir && <p>Source: {site.sourceDir}</p>}
                      {site.buildCommand && <p>Build: {site.buildCommand}</p>}
                      {site.outputDir && <p>Output: {site.outputDir}</p>}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <p className="text-gray-400">No GitHub repository connected</p>
                <p className="text-sm text-gray-500 mt-1">
                  Click <button onClick={() => setShowEditModal(true)} className="text-primary-400 hover:text-primary-300">Edit</button> to add a GitHub repository.
                </p>
              </div>
            )}
          </div>

          {/* Videos */}
          <div className="bg-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Film className="w-5 h-5 text-primary-400" />
                Videos
              </h2>
              <label className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 rounded-lg text-white text-sm cursor-pointer">
                <Upload className="w-4 h-4" />
                {uploadingVideo ? 'Uploading...' : 'Upload Video'}
                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska"
                  onChange={handleVideoUpload}
                  disabled={uploadingVideo}
                  className="hidden"
                />
              </label>
            </div>

            {videoUploadProgress && (
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                <span className="text-sm text-blue-400">{videoUploadProgress}</span>
              </div>
            )}

            {videos.length > 0 ? (
              <div className="space-y-3">
                {videos.map((video) => (
                  <div
                    key={video.id}
                    className="flex items-center gap-4 p-3 rounded-lg bg-gray-700/50"
                  >
                    {/* Thumbnail */}
                    <div className="w-20 h-14 rounded bg-gray-600 flex-shrink-0 overflow-hidden flex items-center justify-center">
                      {video.status === 'READY' ? (
                        <img
                          src={video.thumbnailUrl}
                          alt={video.filename}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).parentElement!.innerHTML = '<svg class="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>';
                          }}
                        />
                      ) : video.status === 'PROCESSING' ? (
                        <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                      ) : (
                        <AlertCircle className="w-6 h-6 text-red-400" />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{video.filename}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            video.status === 'READY'
                              ? 'text-green-400 bg-green-400/10'
                              : video.status === 'PROCESSING'
                              ? 'text-blue-400 bg-blue-400/10'
                              : 'text-red-400 bg-red-400/10'
                          }`}
                        >
                          {video.status}
                        </span>
                        {video.duration && <span>{formatDuration(video.duration)}</span>}
                        {video.width && video.height && (
                          <span>{video.width}x{video.height}</span>
                        )}
                        <span>{formatFileSize(video.size || video.originalSize)}</span>
                      </div>
                      {video.status === 'FAILED' && video.errorMessage && (
                        <p className="text-xs text-red-400 mt-1 truncate">{video.errorMessage}</p>
                      )}
                    </div>

                    {/* Actions */}
                    {video.status === 'READY' && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => {
                            const embedUrl = `https://${site.domain}/embed/${video.id}`;
                            copyToClipboard(
                              `<iframe src="${embedUrl}" width="640" height="360" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>`,
                              `embed-${video.id}`
                            );
                          }}
                          className="p-1.5 text-gray-400 hover:text-primary-400 transition-colors"
                          title="Copy embed code"
                        >
                          {copiedValue === `embed-${video.id}` ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <Code className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            const videoUrl = `https://${site.domain}/videos/${video.filename}`;
                            const posterUrl = `https://${site.domain}/videos/${video.filename}.jpg`;
                            copyToClipboard(
                              `<video autoplay muted loop playsinline preload="auto" fetchpriority="high" poster="${posterUrl}" style="width:100%;height:100%;object-fit:cover">\n  <source src="${videoUrl}" type="video/mp4">\n</video>`,
                              `hero-${video.id}`
                            );
                          }}
                          className="p-1.5 text-gray-400 hover:text-primary-400 transition-colors"
                          title="Copy hero background snippet"
                        >
                          {copiedValue === `hero-${video.id}` ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <Monitor className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => copyToClipboard(`https://${site.domain}/embed/${video.id}`, `url-${video.id}`)}
                          className="p-1.5 text-gray-400 hover:text-primary-400 transition-colors"
                          title="Copy embed URL"
                        >
                          {copiedValue === `url-${video.id}` ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <LinkIcon className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => deleteVideo(video.id, video.filename)}
                          className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                          title="Delete video"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {video.status === 'FAILED' && (
                      <button
                        onClick={() => deleteVideo(video.id, video.filename)}
                        className="p-1.5 text-gray-400 hover:text-red-400 transition-colors flex-shrink-0"
                        title="Delete video"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No videos uploaded yet
              </p>
            )}
          </div>

          {/* Recent Deployments */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Recent Deployments
            </h2>
            <div className="space-y-3">
              {site.deployments && site.deployments.length > 0 ? site.deployments.map((deployment) => (
                <div
                  key={deployment.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-700/50"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          statusColors[deployment.status]
                        }`}
                      >
                        {deployment.status}
                      </span>
                      <span className="text-sm text-gray-400">
                        {deployment.triggeredBy}
                      </span>
                    </div>
                    {deployment.commitMsg && (
                      <p className="text-sm text-gray-300 mt-1 truncate max-w-md">
                        {deployment.commitMsg}
                      </p>
                    )}
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <p>{new Date(deployment.createdAt).toLocaleString()}</p>
                    {deployment.duration && <p>{deployment.duration}s</p>}
                  </div>
                </div>
              )) : (
                <p className="text-gray-500 text-center py-4">
                  No deployments yet
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Site Info */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Site Information
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Slug</dt>
                <dd className="text-white">{site.slug}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Primary Domain</dt>
                <dd className="text-white">{site.domain}</dd>
              </div>
              {site.domains && site.domains.length > 0 && (
                <div>
                  <dt className="text-gray-500">Additional Domains</dt>
                  <dd className="text-white">{site.domains.join(', ')}</dd>
                </div>
              )}
              {site.description && (
                <div>
                  <dt className="text-gray-500">Description</dt>
                  <dd className="text-white">{site.description}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Database</dt>
                <dd className="text-white">
                  {site.hasDatabase ? 'Provisioned' : 'Not provisioned'}
                </dd>
              </div>
              {site.buildCommand && (
                <div>
                  <dt className="text-gray-500">Build Command</dt>
                  <dd className="text-white font-mono text-xs">{site.buildCommand}</dd>
                </div>
              )}
              {site.outputDir && (
                <div>
                  <dt className="text-gray-500">Output Directory</dt>
                  <dd className="text-white font-mono text-xs">{site.outputDir}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Created</dt>
                <dd className="text-white">
                  {new Date(site.createdAt).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>

          {/* DNS Configuration */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              DNS Configuration
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              Add these DNS records in your domain provider (GoDaddy, Cloudflare, etc.) for <span className="font-mono text-white">{site.domain}</span>
            </p>

            <div className="space-y-4">
              {/* A Records */}
              <div className="border border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-400">A Record 1</span>
                  <button
                    onClick={() => copyToClipboard('162.159.140.98', 'ip1')}
                    className="text-gray-400 hover:text-primary-400 transition-colors"
                  >
                    {copiedValue === 'ip1' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Type:</span>
                    <span className="font-mono text-white">A</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Name:</span>
                    <span className="font-mono text-white">@</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Value:</span>
                    <span className="font-mono text-white">162.159.140.98</span>
                  </div>
                </div>
              </div>

              <div className="border border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-400">A Record 2</span>
                  <button
                    onClick={() => copyToClipboard('172.66.0.96', 'ip2')}
                    className="text-gray-400 hover:text-primary-400 transition-colors"
                  >
                    {copiedValue === 'ip2' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Type:</span>
                    <span className="font-mono text-white">A</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Name:</span>
                    <span className="font-mono text-white">@</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Value:</span>
                    <span className="font-mono text-white">172.66.0.96</span>
                  </div>
                </div>
              </div>

              {/* CNAME Record */}
              <div className="border border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-400">CNAME Record (www)</span>
                  <button
                    onClick={() => copyToClipboard('website-paas-vefsj.ondigitalocean.app', 'cname')}
                    className="text-gray-400 hover:text-primary-400 transition-colors"
                  >
                    {copiedValue === 'cname' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Type:</span>
                    <span className="font-mono text-white">CNAME</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Name:</span>
                    <span className="font-mono text-white">www</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Value:</span>
                    <span className="font-mono text-white text-xs">website-paas-vefsj.ondigitalocean.app</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-blue-400">
                <strong>Note:</strong> DNS changes can take 5-15 minutes to propagate. After adding these records, your site will be live at https://{site.domain}
              </p>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-gray-800 rounded-xl p-6 border border-red-500/20">
            <h2 className="text-lg font-semibold text-red-400 mb-4">
              Danger Zone
            </h2>
            <button
              onClick={deleteSite}
              className="flex items-center gap-2 px-4 py-2 bg-red-600/10 hover:bg-red-600/20 text-red-400 rounded-lg w-full justify-center"
            >
              <Trash2 className="w-4 h-4" />
              Delete Site
            </button>
          </div>
        </div>
      </div>

      {/* Edit Site Modal */}
      {showEditModal && site && (
        <EditSiteModal
          site={site}
          onClose={() => setShowEditModal(false)}
          onUpdated={(updated) => {
            setSite(updated);
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
}

interface EditSiteModalProps {
  site: Site;
  onClose: () => void;
  onUpdated: (site: Site) => void;
}

function EditSiteModal({ site, onClose, onUpdated }: EditSiteModalProps) {
  const [form, setForm] = useState({
    name: site.name,
    slug: site.slug,
    domain: site.domain,
    domains: site.domains?.join(', ') || '',
    description: site.description || '',
    githubRepo: site.githubRepo || '',
    githubBranch: site.githubBranch || 'main',
    sourceDir: site.sourceDir || '',
    buildCommand: site.buildCommand || '',
    outputDir: site.outputDir || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const slugChanged = form.slug !== site.slug;
  const domainChanged = form.domain !== site.domain;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const res = await fetch(`/api/sites/${site.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: form.name,
          slug: form.slug,
          domain: form.domain,
          domains: form.domains
            .split(',')
            .map((d) => d.trim())
            .filter(Boolean),
          description: form.description || null,
          githubRepo: form.githubRepo || null,
          githubBranch: form.githubBranch || 'main',
          sourceDir: form.sourceDir || null,
          buildCommand: form.buildCommand || null,
          outputDir: form.outputDir || null,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update site');
      }

      const updated = await res.json();
      onUpdated(updated);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold text-white mb-6">Edit Site</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Information */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Slug
            </label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) =>
                setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })
              }
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
            {slugChanged && (
              <div className="mt-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-xs text-yellow-400">
                  <strong>Warning:</strong> Changing the slug will change the preview URL to /preview/{form.slug}
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Brief description of the site"
              rows={2}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Domain */}
          <div className="pt-4 border-t border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Primary Domain
            </label>
            <input
              type="text"
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
            {domainChanged && (
              <div className="mt-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-xs text-yellow-400">
                  <strong>Warning:</strong> Changing the domain will rename the site folder on disk. Make sure to update your DNS records to point to the new domain.
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Additional Domains
            </label>
            <input
              type="text"
              value={form.domains}
              onChange={(e) => setForm({ ...form, domains: e.target.value })}
              placeholder="www.example.com, other.example.com"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">Comma-separated list of additional domains</p>
          </div>

          {/* GitHub */}
          <div className="pt-4 border-t border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              GitHub Repository
            </label>
            <input
              type="text"
              value={form.githubRepo}
              onChange={(e) => setForm({ ...form, githubRepo: e.target.value })}
              placeholder="owner/repo"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Branch
            </label>
            <input
              type="text"
              value={form.githubBranch}
              onChange={(e) => setForm({ ...form, githubBranch: e.target.value })}
              placeholder="main"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Build Settings */}
          <div className="pt-4 border-t border-gray-700">
            <p className="text-sm font-medium text-gray-400 mb-3">Build Settings</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Source Directory
                </label>
                <input
                  type="text"
                  value={form.sourceDir}
                  onChange={(e) => setForm({ ...form, sourceDir: e.target.value })}
                  placeholder="/"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Build Command
                </label>
                <input
                  type="text"
                  value={form.buildCommand}
                  onChange={(e) => setForm({ ...form, buildCommand: e.target.value })}
                  placeholder="npm run build"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Output Directory
                </label>
                <input
                  type="text"
                  value={form.outputDir}
                  onChange={(e) => setForm({ ...form, outputDir: e.target.value })}
                  placeholder="dist"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="text-red-400 text-sm">{error}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white rounded-lg font-medium"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
